import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type AdminApproval,
  AdminApprovalDecision,
  AdminApprovalType,
  type Invite,
  inviteDeleteInvite,
  inviteListInvitesOptions,
  inviteListInvitesQueryKey,
  inviteResendInviteEmail,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Checkbox,
  CircleQuestionmarkIcon,
  DataTable,
  type DataTableColumnDef,
  DropdownMenuItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { Loading } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import {
  StatusBadge,
  Empty as TableEmpty,
  Timestamp,
  getTimestampColumn,
  tableDefaults,
  useTimeFormat,
} from '~/components/Table';
import {
  AdminApprovalReviewDialog,
  PendingApprovalStatus,
  ReviewChangesButton,
  invalidateAdminApprovals,
  rolesQueryOptions,
  teamQueryKeys,
  useAdminApprovalReview,
  usePendingAdminApprovals,
  usersQueryOptions,
} from '~/lib/approvals';
import { EASE } from '~/lib/approvals/components/ReviewDialog';
import { TableActionDropdown } from '~/lib/components/table/TableActionDropdown';
import { LoginAuthMethod } from '~/lib/session/LoginAuthMethod';
import { DashboardUserRedacted, FactorTypeResponse, UserRepository } from '~/repositories';
import { OwnerRoleId, getRoleLabel, precannedRolesByID, precannedRolesByName } from '~/repositories/RoleRepository';
import { Factor, MFAStatus } from '~/repositories/UserRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { InviteApprovalBatchDialog, type InviteApprovalBatchRequest } from './_components/InviteApprovalBatchDialog';
import { InviteUsersModal, type RoleOption } from './_components/InviteUsersModal';
import { useInviteApprovalSelection } from './_hooks/useInviteApprovalSelection';

interface UserListInfo {
  id: string;
  name: string;
  isOwner: boolean;
}

interface UserListRole {
  id: string;
  name: string;
}

const NO_APPROVALS: AdminApproval[] = [];

const INVITE_REQUIRES_MFA_TOOLTIP = 'Enable two-factor authentication in your profile to invite users.';
const MANAGE_USERS_PERMISSION_TOOLTIP = 'You do not have permission to manage users.';

interface UserRow {
  id: string;
  info: UserListInfo;
  email: string;
  factors: Factor[];
  role: UserListRole;
  user: DashboardUserRedacted;
}

interface InviteRow {
  id: string;
  email: string;
  platformRole: string;
  expiredAt?: Date;
  created?: Date;
  createdBy?: string;
  invite?: Invite;
  approval?: AdminApproval;
}

const columnWidths = {
  primary: 'minmax(300px, 1fr)',
  secondary: 'minmax(240px, 1fr)',
  tertiary: 'minmax(160px, 1fr)',
  last: 'minmax(260px, 300px)',
};

const InviteSelectCell: React.FC<{
  checked: boolean | 'indeterminate';
  label: string;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ checked, label, disabled, onCheckedChange }) => (
  <label
    className={cn(
      '-my-2 -ml-2 flex items-center self-stretch py-2 pr-2 pl-2',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
    )}
    onClick={(event) => event.stopPropagation()}
  >
    <Checkbox
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onCheckedChange={(value) => onCheckedChange(value === true)}
    />
  </label>
);

// Disabled controls don't fire pointer events, so the tooltip hangs off a wrapper instead.
const ReasonTooltip: React.FC<
  React.PropsWithChildren<{ content?: string; focusable?: boolean; className?: string }>
> = ({ content, focusable, className, children }) =>
  content ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={focusable ? 0 : undefined} className={cn('flex', className)}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    children
  );

interface RowActionOption {
  label: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isDanger?: boolean;
  tooltip?: string;
}

const RowActions: React.FC<{ options: RowActionOption[] }> = ({ options }) =>
  options.length > 0 ? (
    <TableActionDropdown>
      {options.map((option) => (
        <ReasonTooltip key={option.label} content={option.isDisabled ? option.tooltip : undefined} className="block">
          <DropdownMenuItem
            variant={option.isDanger ? 'destructive' : 'default'}
            disabled={option.isDisabled}
            onSelect={option.onClick}
          >
            {option.label}
          </DropdownMenuItem>
        </ReasonTooltip>
      ))}
    </TableActionDropdown>
  ) : null;

const RoleHeader: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex cursor-pointer items-center gap-1 transition-colors hover:text-foreground"
  >
    <CircleQuestionmarkIcon />
    Role
  </button>
);

const TeamSection: React.FC<React.PropsWithChildren<{ title: string; border?: boolean }>> = ({
  title,
  border,
  children,
}) => (
  <section className={cn('px-(--page-margin) pb-6', border && 'border-t border-gray-150 dark:border-white/10')}>
    <h2 className="m-0 py-4 text-base leading-5 font-medium tracking-[-0.02em] text-foreground">{title}</h2>
    {children}
  </section>
);

export const PageTeam: React.FC = () => {
  const { currentUser, currentPlatform, isAuthorized, isSandbox, mfaStatus, loginAuthMethod } = useSessionStore();
  const canWriteUsers = isAuthorized({ permission: 'users', permissionLevel: 'write' });
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const queryClient = useQueryClient();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const pendingInvites = usePendingAdminApprovals(AdminApprovalType.USER_INVITE);
  const selectionContext = `${currentPlatform?.id}:${currentUser?.id}:${isSandbox}`;
  const canReviewWithMfa =
    mfaStatus === MFAStatus.VERIFIED ||
    mfaStatus === MFAStatus.VERIFIED_LEGACY ||
    loginAuthMethod === LoginAuthMethod.PASSKEY;
  const inviteSelection = useInviteApprovalSelection(
    canReviewWithMfa ? pendingInvites : NO_APPROVALS,
    selectionContext
  );
  const hasInviteSelection = inviteSelection.eligible.length > 0;
  const [batchRequest, setBatchRequest] = useState<InviteApprovalBatchRequest & { context: string }>();
  useEffect(() => setBatchRequest(undefined), [selectionContext]);
  const openBatchReview = (decision: AdminApprovalDecision) => {
    if (inviteSelection.selected.length > 0) {
      setBatchRequest({
        approvals: inviteSelection.selected,
        decision,
        context: selectionContext,
        roleLabels: Object.fromEntries(inviteList.map((row) => [row.id, getRoleLabel(row.platformRole)])),
      });
    }
  };
  const pendingRoleAssigns = usePendingAdminApprovals(AdminApprovalType.USER_ROLE_ASSIGN);
  const pendingRoleAssignByUser = useMemo(
    () => new Map(pendingRoleAssigns.map((a) => [a.request.user_role_assign?.dashboard_user_id, a])),
    [pendingRoleAssigns]
  );
  const visibleApprovals = useMemo(
    () => [...pendingRoleAssigns, ...pendingInvites],
    [pendingInvites, pendingRoleAssigns]
  );
  const { openReview, getApprovalActions, startReview, dialogProps } = useAdminApprovalReview(visibleApprovals);
  const openInviteModal = useCallback(() => setIsInviteModalOpen(true), []);
  const closeInviteModal = useCallback(() => setIsInviteModalOpen(false), []);

  const hasMfaEnabled = useMemo(
    () => currentUser?.factors?.some((factor) => factor.factorVerified) ?? false,
    [currentUser]
  );

  const {
    data: usersResponse,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useQuery({
    ...usersQueryOptions(currentPlatform?.id),
  });

  const {
    data: invitesResponse,
    isLoading: isInvitesLoading,
    isFetching: isInvitesFetching,
  } = useQuery({
    ...inviteListInvitesOptions(),
    enabled: !!currentPlatform,
  });

  const invites = invitesResponse?.invites ?? [];

  const { data: rolesResponse } = useQuery(rolesQueryOptions());

  const rolesList: RoleOption[] = useMemo(() => {
    return (rolesResponse?.roles || []).map((role) => ({
      label: getRoleLabel(role.name),
      value: role.id,
    }));
  }, [rolesResponse]);

  const deleteInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return inviteDeleteInvite({ path: { platform_id: currentPlatform.id, email }, throwOnError: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteListInvitesQueryKey() });
      addSuccessNotification({
        content: 'Invite deleted',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to delete invite',
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return inviteResendInviteEmail({ body: { email }, throwOnError: true });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Invite resent',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to resend invite',
      });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      if (!currentPlatform) throw new Error('No platform selected');
      return UserRepository.updatePlatformRole(currentPlatform.id, {
        userId,
        customRoleId: precannedRolesByID[roleId] ? undefined : roleId,
        precannedRoleName: precannedRolesByID[roleId] ? precannedRolesByID[roleId].name : undefined,
      });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      invalidateAdminApprovals(queryClient);
      const role = rolesResponse?.roles?.find((r) => r.id === variables.roleId);
      if (response.pendingApproval) {
        addSuccessNotification({
          content: `Role change to "${role ? getRoleLabel(role.name) : 'new role'}" submitted for approval.`,
        });
      } else if (role) {
        addSuccessNotification({
          content: `Role updated to "${getRoleLabel(role.name)}" successfully.`,
        });
      }
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to update role',
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async ({ user, platformId }: { user: DashboardUserRedacted; platformId: string }) => {
      return UserRepository.removeUserFromPlatform({
        dashboardUserId: user.id,
        platformId,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      addSuccessNotification({
        content: `"${variables.user.firstName} ${variables.user.lastName}" successfully removed from this platform.`,
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to remove user',
      });
    },
  });

  const deleteFactorMutation = useMutation({
    mutationFn: async ({ factorId }: { factorId: string }) => {
      return UserRepository.deleteFactor({ factorId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.users(currentPlatform?.id) });
      addSuccessNotification({
        content: 'Authenticator app disabled',
      });
    },
    onError: (error: any) => {
      addDangerNotification({
        content: error.message || 'Failed to disable authenticator',
      });
    },
  });

  const handleRoleChange = useCallback(
    (userId: string, email: string, roleId: string) => {
      if (!currentPlatform || !rolesResponse) return;
      updateRoleMutation.mutate({ userId, roleId });
    },
    [currentPlatform, rolesResponse, updateRoleMutation]
  );

  const handleRemoveUser = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      removeUserMutation.mutate({ user, platformId });
    },
    [removeUserMutation]
  );

  const handleUserRemoveConfirm = useCallback(
    (user: DashboardUserRedacted, platformId: string) => {
      openAlert({
        headline: `Remove ${user.firstName}?`,
        content: `Are you sure you want to remove ${user.firstName} ${user.lastName} from this platform?`,
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => handleRemoveUser(user, platformId),
      });
    },
    [handleRemoveUser, openAlert]
  );

  const handleUserDisableTotpMfa = useCallback(
    (user: DashboardUserRedacted, totpFactor: Factor) => {
      openAlert({
        headline: `Disable Authenticator App for ${user.firstName}`,
        content: `This action cannot be undone. If authenticator app two-factor authentication is disabled, it can
          only be re-enabled by ${user.firstName}.`,
        confirmButton: {
          label: 'Disable',
          variant: 'danger',
        },
        onSuccess: () => {
          deleteFactorMutation.mutate({ factorId: totpFactor.factorId });
        },
      });
    },
    [deleteFactorMutation, openAlert]
  );

  const deleteInvite = useCallback(
    (email: string) => {
      deleteInviteMutation.mutate(email);
    },
    [deleteInviteMutation]
  );

  const resendInvite = useCallback(
    (email: string) => {
      resendInviteMutation.mutate(email);
    },
    [resendInviteMutation]
  );

  const userList: UserRow[] = useMemo(() => {
    if (!currentUser || !usersResponse) return [];
    return usersResponse.dashboardUsers.map((user) => ({
      id: user.id,
      info: {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        isOwner: user.platformRole.name === 'owner',
      },
      email: user.email,
      factors: user.factors,
      role: {
        id: user.platformRole.id,
        name: user.platformRole.name,
      },
      user,
    }));
  }, [currentUser, usersResponse]);

  const userColumns: DataTableColumnDef<UserRow>[] = useMemo(
    () =>
      currentPlatform
        ? [
            {
              header: 'Name',
              accessor: 'info',
              width: columnWidths.primary,
              cell: ({ value }) => (
                <DataTable.TextCell value={value.name} weight="medium">
                  {(value.id === currentUser?.id || value.isOwner) && (
                    <span className="flex gap-1">
                      {value.id === currentUser?.id && <StatusBadge tone="info">You</StatusBadge>}
                      {value.isOwner && <StatusBadge>Owner</StatusBadge>}
                    </span>
                  )}
                </DataTable.TextCell>
              ),
            },
            {
              header: 'Email',
              accessor: 'email',
              width: columnWidths.secondary,
              cell: ({ value }) => <DataTable.TextCell value={value} />,
            },
            {
              header: '2FA',
              accessor: 'factors',
              width: columnWidths.tertiary,
              cell: ({ value }) => {
                const factors: Factor[] = value ?? [];
                if (factors.length === 0) {
                  return <StatusBadge>Not enabled</StatusBadge>;
                }
                if (
                  factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.WEBAUTHN)
                ) {
                  return <StatusBadge tone="success">Passkey</StatusBadge>;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP)) {
                  return <StatusBadge tone="success">Authenticator</StatusBadge>;
                }
                if (factors.some((factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS)) {
                  return <StatusBadge tone="success">SMS</StatusBadge>;
                }
                return null;
              },
            },
            {
              header: <RoleHeader onClick={() => openHelpSidebar('Roles')} />,
              accessor: 'role',
              width: columnWidths.last,
              cell: ({ row, value }) => {
                const activeRoleId = value.id === OwnerRoleId ? precannedRolesByName.admin.id : value.id;
                const isDisabled =
                  !canWriteUsers ||
                  currentUser?.id === value.id ||
                  (value.id === OwnerRoleId && currentPlatform.id === currentUser?.defaultPlatformId);
                const pendingAssign = pendingRoleAssignByUser.get(row.id);
                if (pendingAssign) {
                  return (
                    <DataTable.Cell className="px-0">
                      <PendingApprovalStatus />
                    </DataTable.Cell>
                  );
                }
                return (
                  <ReasonTooltip
                    content={canWriteUsers ? undefined : MANAGE_USERS_PERMISSION_TOOLTIP}
                    focusable
                    className="w-full"
                  >
                    <Select
                      value={activeRoleId}
                      onValueChange={(roleId) => handleRoleChange(row.id, row.email, roleId)}
                      disabled={isDisabled}
                    >
                      <SelectTrigger size="sm" variant="outline" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rolesList.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </ReasonTooltip>
                );
              },
            },
          ]
        : [],
    [currentPlatform, currentUser, canWriteUsers, rolesList, handleRoleChange, openHelpSidebar, pendingRoleAssignByUser]
  );

  const userRowAction = useCallback(
    (row: UserRow) => {
      const smsFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.SMS
      );
      const totpFactor = row.user.factors.find(
        (factor) => factor.factorVerified && factor.factorType === FactorTypeResponse.TOTP
      );

      const pendingAssign = pendingRoleAssignByUser.get(row.id);

      return (
        <RowActions
          options={[
            ...(pendingAssign ? getApprovalActions(pendingAssign) : []),
            {
              label: 'Reset Authenticator 2FA',
              isDisabled: !canWriteUsers || !smsFactor || !totpFactor,
              tooltip: !canWriteUsers
                ? MANAGE_USERS_PERMISSION_TOOLTIP
                : !totpFactor
                  ? 'User does not have Authenticator 2FA configured'
                  : !smsFactor
                    ? 'Authenticator 2FA may not be reset without SMS 2FA enabled'
                    : undefined,
              onClick: totpFactor ? () => handleUserDisableTotpMfa(row.user, totpFactor) : undefined,
            },
            {
              label: 'Remove',
              isDanger: true,
              isDisabled: !canWriteUsers || currentUser?.id === row.user.id || row.user.platformRole.name === 'owner',
              tooltip: !canWriteUsers ? MANAGE_USERS_PERMISSION_TOOLTIP : undefined,
              onClick: () => handleUserRemoveConfirm(row.user, row.user.platformRole.platformId),
            },
          ]}
        />
      );
    },
    [
      getApprovalActions,
      canWriteUsers,
      currentUser,
      handleUserDisableTotpMfa,
      handleUserRemoveConfirm,
      pendingRoleAssignByUser,
    ]
  );

  const inviteList: InviteRow[] = useMemo(
    () => [
      ...pendingInvites.map((approval) => {
        const request = approval.request.user_invite;
        return {
          id: approval.id,
          email: request?.email ?? '',
          platformRole:
            request?.platform_role ?? rolesResponse?.roles?.find((role) => role.id === request?.role_id)?.name ?? '',
          created: new Date(approval.created_at),
          createdBy: approval.requested_by.name,
          approval,
        };
      }),
      ...invites.map((invite) => {
        const created = invite.created_at ? new Date(invite.created_at) : undefined;

        return {
          id: `invite:${invite.email}`,
          email: invite.email,
          platformRole: invite.platform_role,
          expiredAt: invite.expired_at ? new Date(invite.expired_at) : undefined,
          created,
          createdBy: invite.created_by?.name,
          invite,
        };
      }),
    ],
    [invites, pendingInvites, rolesResponse]
  );

  const { timeFormat, onTimeFormatChange } = useTimeFormat('team-invites');

  const inviteRowAction = useCallback(
    (row: InviteRow) => {
      const { approval } = row;
      if (approval) {
        return <RowActions options={getApprovalActions(approval)} />;
      }
      return (
        <RowActions
          options={[
            {
              label: 'Resend',
              isDisabled: !canWriteUsers,
              tooltip: !canWriteUsers ? MANAGE_USERS_PERMISSION_TOOLTIP : undefined,
              onClick: () => resendInvite(row.email),
            },
            {
              label: 'Delete',
              isDisabled: !canWriteUsers,
              tooltip: !canWriteUsers ? MANAGE_USERS_PERMISSION_TOOLTIP : undefined,
              onClick: () => deleteInvite(row.email),
              isDanger: true,
            },
          ]}
        />
      );
    },
    [getApprovalActions, canWriteUsers, deleteInvite, resendInvite]
  );

  const inviteColumns: DataTableColumnDef<InviteRow>[] = useMemo(
    () => [
      {
        header: hasInviteSelection ? (
          <span className="flex items-center">
            <InviteSelectCell
              checked={inviteSelection.allSelected}
              label="Select invites to review"
              disabled={batchRequest?.context === selectionContext}
              onCheckedChange={(checked) =>
                inviteSelection.setSelected(
                  inviteSelection.eligible.map((approval) => approval.id),
                  checked
                )
              }
            />
            Email
          </span>
        ) : (
          'Email'
        ),
        accessor: 'email',
        width: columnWidths.primary,
        cell: ({ row, value }) => (
          <>
            {hasInviteSelection && (
              <InviteSelectCell
                checked={inviteSelection.selectedIds.has(row.id)}
                label={`Select invite for ${row.email}`}
                disabled={
                  batchRequest?.context === selectionContext ||
                  !inviteSelection.eligibleIds.has(row.id) ||
                  (inviteSelection.atLimit && !inviteSelection.selectedIds.has(row.id))
                }
                onCheckedChange={(checked) => inviteSelection.setSelected([row.id], checked)}
              />
            )}
            <DataTable.TextCell value={value} weight="medium" />
          </>
        ),
      },
      {
        header: 'Status',
        accessor: 'expiredAt',
        width: columnWidths.secondary,
        cell: ({ row, value }) => (
          <DataTable.Cell>
            {row.approval ? (
              <PendingApprovalStatus />
            ) : value && value < new Date() ? (
              <StatusBadge tone="danger">Expired</StatusBadge>
            ) : (
              <StatusBadge tone="success">Invite sent</StatusBadge>
            )}
          </DataTable.Cell>
        ),
      },
      {
        header: <RoleHeader onClick={() => openHelpSidebar('Roles')} />,
        accessor: 'platformRole',
        width: columnWidths.tertiary,
        cell: ({ value }) => <StatusBadge>{getRoleLabel(value)}</StatusBadge>,
      },
      {
        ...getTimestampColumn<InviteRow>({ accessor: 'created', timeFormat, onTimeFormatChange }),
        width: columnWidths.last,
        cell: ({ row, value }) => (
          <DataTable.Cell>
            <span className="flex items-center gap-1 whitespace-nowrap">
              {row.createdBy && (
                <>
                  <span className="text-xs text-muted-foreground">{row.createdBy}</span>
                  <span aria-hidden className="text-xs text-muted-foreground select-none">
                    ·
                  </span>
                </>
              )}
              <Timestamp date={value} timeFormat={timeFormat} />
            </span>
          </DataTable.Cell>
        ),
      },
    ],
    [
      onTimeFormatChange,
      openHelpSidebar,
      timeFormat,
      inviteSelection,
      batchRequest,
      selectionContext,
      hasInviteSelection,
    ]
  );

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} />
      {batchRequest && batchRequest.context === selectionContext && (
        <InviteApprovalBatchDialog
          key={selectionContext}
          request={batchRequest}
          onClose={() => setBatchRequest(undefined)}
          onReviewed={(id) => inviteSelection.setSelected([id], false)}
        />
      )}
      <InviteUsersModal
        onClose={closeInviteModal}
        onSave={() => {
          closeInviteModal();
          queryClient.invalidateQueries({ queryKey: inviteListInvitesQueryKey() });
          invalidateAdminApprovals(queryClient);
        }}
        open={isInviteModalOpen}
        roles={rolesList}
      />
      <PageActions>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={inviteSelection.selected.length > 0 ? 'selection' : 'review'}
            initial={{ opacity: 0, x: 8, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.95, transition: { duration: 0.1, ease: EASE } }}
            transition={{ duration: 0.15, ease: EASE }}
            className="flex items-center gap-2"
          >
            {inviteSelection.selected.length > 0 ? (
              <>
                <div className="mr-1.5 flex items-center gap-2.5">
                  <span className="text-sm font-medium whitespace-nowrap text-muted-foreground">
                    {inviteSelection.selected.length} {inviteSelection.selected.length === 1 ? 'invite' : 'invites'}{' '}
                    selected{inviteSelection.atLimit ? ' (maximum)' : ''}
                  </span>
                  <Button size="sm" variant="subtle-primary" className="h-auto px-0" onClick={inviteSelection.clear}>
                    Reset
                  </Button>
                </div>
                <Button size="sm" variant="danger" onClick={() => openBatchReview(AdminApprovalDecision.REJECTED)}>
                  <Icon.Cross className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                  Reject
                </Button>
                <Button size="sm" onClick={() => openBatchReview(AdminApprovalDecision.APPROVED)}>
                  <Icon.Checkmark className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                  Approve
                </Button>
              </>
            ) : (
              <>
                <ReviewChangesButton onClick={startReview} />
                <ReasonTooltip
                  content={
                    !canWriteUsers
                      ? MANAGE_USERS_PERMISSION_TOOLTIP
                      : currentUser && !hasMfaEnabled
                        ? INVITE_REQUIRES_MFA_TOOLTIP
                        : undefined
                  }
                  focusable
                >
                  <Button onClick={openInviteModal} size="sm" disabled={!canWriteUsers || !hasMfaEnabled}>
                    <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
                    Invite User
                  </Button>
                </ReasonTooltip>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </PageActions>
      <Loading isLoading={isUsersLoading || isInvitesLoading}>
        <TeamSection title="Users">
          <DataTable<UserRow>
            {...tableDefaults}
            columns={userColumns}
            data={userList}
            isLoading={isUsersFetching}
            rowKey={(row) => row.id}
            rowAction={userRowAction}
            rowClassName={(row) =>
              pendingRoleAssignByUser.has(row.id) ? 'group/row min-h-11' : 'min-h-11 cursor-default'
            }
            onRowClick={(row) => {
              const pendingAssign = pendingRoleAssignByUser.get(row.id);
              if (pendingAssign) openReview(pendingAssign);
            }}
            emptyContent={<TableEmpty icon={<Icon.AnimationUser />} title="No team members found" />}
          />
        </TeamSection>
        <TeamSection title="Pending Invites" border>
          <DataTable<InviteRow>
            {...tableDefaults}
            columns={inviteColumns}
            data={inviteList}
            isLoading={isInvitesFetching}
            rowKey={(row) => row.id}
            rowAction={inviteRowAction}
            rowClassName={(row) => (row.approval ? 'group/row min-h-11' : 'min-h-11 cursor-default')}
            onRowClick={(row) => row.approval && openReview(row.approval)}
            emptyMessage="No pending invites"
          />
        </TeamSection>
      </Loading>
    </>
  );
};
